// src/lib/plugins/revenuecat.ts
import { Capacitor, registerPlugin } from "@capacitor/core";

// --- TYPES (unchanged from your version) ---
export enum LOG_LEVEL {
  DEBUG = "DEBUG",
  INFO = "INFO",
  WARN = "WARN",
  ERROR = "ERROR",
}
export type LogLevel = `${LOG_LEVEL}`;

export type PurchasesCustomerInfo = {
  entitlements?: { active?: Record<string, any> };
  activeSubscriptions?: string[];
  [k: string]: unknown;
};

export type PurchasesPackage = {
  identifier?: string;
  product?: unknown;
  [k: string]: unknown;
};

export type PurchasesOffering = {
  identifier?: string;
  availablePackages?: PurchasesPackage[];
  [k: string]: unknown;
};

export type PurchasesOfferings = {
  current: PurchasesOffering | null;
  all: Record<string, PurchasesOffering>;
};

type NativePurchases = {
  setLogLevel(opts: { level: LogLevel }): Promise<void>;
  configure(opts: {
    apiKey: string;
    appUserID?: string;
    usesStoreKit2IfAvailable?: boolean;
  }): Promise<void>;
  getCustomerInfo(): Promise<{ customerInfo: PurchasesCustomerInfo }>;
  getOfferings():
    | Promise<{ offerings: PurchasesOfferings }>
    | Promise<PurchasesOfferings>;
  purchasePackage(opts: {
    aPackage: PurchasesPackage;
  }): Promise<{ customerInfo: PurchasesCustomerInfo }>;
  restorePurchases(): Promise<{ customerInfo: PurchasesCustomerInfo }>;
  logIn(opts: { appUserID: string }): Promise<{ customerInfo: PurchasesCustomerInfo }>;
  logOut(): Promise<{ customerInfo: PurchasesCustomerInfo }>;
  setAttributes?(attributes: Record<string, string>): Promise<void>;
};

type RCState = "idle" | "configuring" | "configured" | "failed";

// --- INTERNAL STATE ---
let state: RCState = "idle";
let configured = false;
let lastError: string | null = null;
let lastApiKey: string | null = null;
let lastAppUserID: string | undefined;

let cachedNative: NativePurchases | null = null;
let loadNativePromise: Promise<NativePurchases | null> | null = null;
let ensureConfiguredPromise: Promise<boolean> | null = null;

// --- HELPERS ---
function isRealPromise(obj: any): obj is Promise<any> {
  return !!obj && Object.prototype.toString.call(obj) === "[object Promise]";
}

function withTimeout<T>(p: Promise<T> | T, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`[RC] ${label} TIMEOUT after ${ms}ms`)), ms);
    try {
      if (isRealPromise(p)) {
        p.then(
          (v) => { clearTimeout(t); resolve(v); },
          (e) => { clearTimeout(t); reject(e); }
        );
      } else {
        clearTimeout(t);
        resolve(p as T);
      }
    } catch (err) {
      clearTimeout(t);
      reject(err);
    }
  });
}

// --- LOAD NATIVE PLUGIN ---
async function loadNative(): Promise<NativePurchases | null> {
  const platform = Capacitor.getPlatform?.();
  if (platform !== "ios") return null;
  if (cachedNative) return cachedNative;
  if (loadNativePromise) return loadNativePromise;

  console.log("[RC] loadNative starting, platform:", platform);

  loadNativePromise = (async () => {
    let rawPlugin: any = null;

    try {
      const mod: any = await withTimeout(
        import("@revenuecat/purchases-capacitor"),
        5000,
        "direct-import"
      );
      rawPlugin = mod?.Purchases;
      const must = [
        "configure",
        "getOfferings",
        "getCustomerInfo",
        "purchasePackage",
        "restorePurchases",
        "logIn",
        "logOut",
        "setLogLevel",
      ] as const;
      if (rawPlugin && must.every((m) => typeof rawPlugin[m] === "function")) {
        console.log("[RC] Native plugin OK (direct import)");
      } else {
        rawPlugin = null;
      }
    } catch (e) {
      console.warn("[RC] direct import failed:", (e as Error)?.message ?? e);
      rawPlugin = null;
    }

    if (!rawPlugin) {
      try {
        rawPlugin = registerPlugin("Purchases");
        const must = [
          "configure",
          "getOfferings",
          "getCustomerInfo",
          "purchasePackage",
          "restorePurchases",
          "logIn",
          "logOut",
          "setLogLevel",
        ] as const;
        if (must.every((m) => typeof rawPlugin?.[m] === "function")) {
          console.log("[RC] Native plugin OK (registerPlugin)");
        } else {
          throw new Error("registerPlugin missing required methods");
        }
      } catch (e) {
        console.error("[RC] registerPlugin failed:", (e as Error)?.message ?? e);
        return null;
      }
    }

    if (!rawPlugin) return null;

    // DE-THENIFY: Wrap the raw Capacitor proxy in a plain object
    // This prevents JavaScript from treating it as a thenable Promise
    const wrappedPlugin: NativePurchases = {
      configure: rawPlugin.configure.bind(rawPlugin),
      getCustomerInfo: rawPlugin.getCustomerInfo.bind(rawPlugin),
      getOfferings: rawPlugin.getOfferings.bind(rawPlugin),
      purchasePackage: rawPlugin.purchasePackage.bind(rawPlugin),
      restorePurchases: rawPlugin.restorePurchases.bind(rawPlugin),
      logIn: rawPlugin.logIn.bind(rawPlugin),
      logOut: rawPlugin.logOut.bind(rawPlugin),
      setLogLevel: rawPlugin.setLogLevel.bind(rawPlugin),
      setAttributes: rawPlugin.setAttributes?.bind(rawPlugin),
    };

    cachedNative = wrappedPlugin;
    return cachedNative;
  })();

  const result = await loadNativePromise.finally(() => {
    loadNativePromise = null;
  });
  return result;
}

// --- CONFIGURATION ---
export async function ensureConfigured(apiKey: string, appUserID?: string): Promise<boolean> {
  if (configured) return true;
  if (!ensureConfiguredPromise) {
    ensureConfiguredPromise = (async () => {
      state = "configuring";
      try {
        const native = await loadNative();
        if (!native) {
          state = "failed";
          lastError = "NATIVE_PLUGIN_MISSING";
          return false;
        }
        const logLevel = import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.ERROR;
        await withTimeout(native.setLogLevel({ level: logLevel }), 5000, "setLogLevel");
        await withTimeout(
          native.configure({
            apiKey,
            appUserID,
            usesStoreKit2IfAvailable: true,
          }),
          15000,
          "configure"
        );
        state = "configured";
        configured = true;
        lastApiKey = apiKey;
        lastAppUserID = appUserID;
        console.log("[RC] configure SUCCESS");
        return true;
      } catch (e) {
        state = "failed";
        lastError = (e as Error)?.message || String(e);
        ensureConfiguredPromise = null;
        console.error("[RC] configure failed:", lastError);
        return false;
      }
    })();
  }
  return ensureConfiguredPromise;
}

async function autoConfigureIfNeeded() {
  if (!configured && lastApiKey) {
    console.log("[RC] Auto-configure on demand…");
    await ensureConfigured(lastApiKey, lastAppUserID);
  }
}

// --- PUBLIC API ---
export const Purchases = {
  async configure(opts: { apiKey: string; appUserID?: string; usesStoreKit2IfAvailable?: boolean }) {
    lastApiKey = opts.apiKey;
    lastAppUserID = opts.appUserID;
    const ok = await ensureConfigured(opts.apiKey, opts.appUserID);
    return ok ? { ok: true } : { ok: false, reason: "failed" };
  },

  async getCustomerInfo() {
    await autoConfigureIfNeeded();
    console.log("[RC DEBUG] Calling getCustomerInfo()");
    const n = await loadNative();
    if (!n) throw new Error("Purchases native missing");
    return n.getCustomerInfo();
  },

  async getOfferings() {
    await autoConfigureIfNeeded();
    console.log("[RC DEBUG] Calling getOfferings()");
    const n = await loadNative();
    if (!n) throw new Error("Purchases native missing");
    const res = await n.getOfferings();
    return (res as any)?.offerings ?? res;
  },

  async purchasePackage(input: PurchasesPackage | string, cachedOfferings?: PurchasesOfferings) {
    await autoConfigureIfNeeded();
    console.log("[RC DEBUG] Calling purchasePackage()");
    const n = await loadNative();
    if (!n) throw new Error("Purchases native missing");
    // keep your existing purchase resolution logic
    if (typeof input === "string") {
      const offeringsObj =
        cachedOfferings ?? ((await n.getOfferings()) as any)?.offerings ?? (await n.getOfferings());
      const allPkgs = Object.values((offeringsObj as PurchasesOfferings).all || {}).flatMap(
        (o) => o?.availablePackages || []
      );
      const match = allPkgs.find((p) => p.identifier === input);
      if (!match) throw new Error(`[RC] No package found for identifier: ${input}`);
      return n.purchasePackage({ aPackage: match });
    } else {
      return n.purchasePackage({ aPackage: input });
    }
  },

  async restorePurchases() {
    await autoConfigureIfNeeded();
    console.log("[RC DEBUG] Calling restorePurchases()");
    const n = await loadNative();
    if (!n) throw new Error("Purchases native missing");
    return n.restorePurchases();
  },

  async logIn(o: { appUserID: string }) {
    lastAppUserID = o.appUserID;
    await autoConfigureIfNeeded();
    console.log("[RC DEBUG] Calling logIn()");
    const n = await loadNative();
    if (!n) throw new Error("Purchases native missing");
    return n.logIn(o);
  },

  async logOut() {
    await autoConfigureIfNeeded();
    console.log("[RC DEBUG] Calling logOut()");
    const n = await loadNative();
    if (!n) throw new Error("Purchases native missing");
    return n.logOut();
  },

  async setAttributes(attributes: Record<string, string>) {
    await autoConfigureIfNeeded();
    console.log("[RC DEBUG] Calling setAttributes()");
    const n = await loadNative();
    if (!n) throw new Error("Purchases native missing");
    return n.setAttributes?.(attributes);
  },
};

// --- DEBUG ---
export function isConfigured(): boolean {
  return configured;
}

export function __debugStatus() {
  return {
    platform: Capacitor.getPlatform?.(),
    configured,
    state,
    hasNative: !!cachedNative,
    lastError,
  };
}
